/**
 * Wire quantisation. The filter emits values that are already quantised, so a snapshot
 * that goes through the binary codec decodes to exactly the object the filter produced
 * (the in-process harness path and the network path give bots identical views).
 */

const TAU = Math.PI * 2;

/** Clamp to i16; also folds −0 into 0 so quantised values survive a strict round trip. */
const clampI16 = (v: number) => (v < -32768 ? -32768 : v > 32767 ? 32767 : v || 0);

/** Positions (mu): i16 × 100 → 0.01 mu resolution, range ±327 mu. */
export const POS_SCALE = 100;
/** Velocities (mu/s): i16 × 1000 → range ±32 mu/s. */
export const VEL_SCALE = 1000;
/** Area shares: u16 × 1e4. */
export const SHARE_SCALE = 10_000;
/** Angles: u16 over a full turn. */
export const ANGLE_STEPS = 65_536;
/** Hint spread: u8 in half-degree steps. */
export const SPREAD_STEP = Math.PI / 360;

export const posToWire = (v: number) => clampI16(Math.round(v * POS_SCALE));
export const posFromWire = (w: number) => w / POS_SCALE;
export const qPos = (v: number) => posFromWire(posToWire(v));

export const velToWire = (v: number) => clampI16(Math.round(v * VEL_SCALE));
export const velFromWire = (w: number) => w / VEL_SCALE;
export const qVel = (v: number) => velFromWire(velToWire(v));

export const shareToWire = (v: number) => Math.min(SHARE_SCALE, Math.max(0, Math.round(v * SHARE_SCALE)));
export const shareFromWire = (w: number) => w / SHARE_SCALE;
export const qShare = (v: number) => shareFromWire(shareToWire(v));

/** Any angle (radians) → u16 step in [0, 65535]. */
export function angleToWire(a: number): number {
  let t = a % TAU;
  if (t < 0) t += TAU;
  return Math.round((t / TAU) * ANGLE_STEPS) % ANGLE_STEPS;
}
export const angleFromWire = (w: number) => (w / ANGLE_STEPS) * TAU;
export const qAngle = (a: number) => angleFromWire(angleToWire(a));

export const spreadToWire = (s: number) => Math.min(255, Math.max(0, Math.round(s / SPREAD_STEP)));
export const spreadFromWire = (w: number) => w * SPREAD_STEP;
export const qSpread = (s: number) => spreadFromWire(spreadToWire(s));

/** Focused-front readout: status (2 bits) | pressure ratio × 10 (6 bits) in a u8; rate as i8 hex/min. */
export const FRONT_RATIO_STEP = 0.1;
const FRONT_RATIO_MAX = 63;
export const frontRatioToWire = (r: number) => Math.min(FRONT_RATIO_MAX, Math.max(0, Math.round(r / FRONT_RATIO_STEP)));
export const frontRatioFromWire = (w: number) => Math.round(w * FRONT_RATIO_STEP * 10) / 10;
export const frontRateToWire = (r: number) => Math.min(127, Math.max(-127, Math.round(r))) || 0;

/** Density / radiation fraction (0..1) → 0..15 nibble. */
export const nibble = (f: number) => (f <= 0 ? 0 : f >= 1 ? 15 : Math.round(f * 15));
